import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { InternalHoursBalance } from '@/types'

const HOURS_PER_DAY = 8

const formatHoursToDays = (hours: number): string => {
  if (!hours) return '0'

  const sign = hours < 0 ? '-' : ''
  const absHours = Math.abs(hours)
  const days = Math.floor(absHours / HOURS_PER_DAY)
  const remainingHours = Math.round((absHours % HOURS_PER_DAY) * 100) / 100

  if (days > 0 && remainingHours > 0)
    return `${sign}${days}d ${remainingHours}h`
  if (days > 0) return `${sign}${days}d`

  return `${sign}${remainingHours}h`
}

type Props = {
  balance: InternalHoursBalance
  title: string
}

export const HolidayBalanceCard = ({ balance, title }: Props): JSX.Element => {
  const { t } = useTranslation('internalHours')

  const stats = [
    {
      label: t('balance.normalHoliday'),
      value: formatHoursToDays(balance.normal_holiday_hours),
    },
    {
      label: t('balance.nationalHoliday'),
      value: formatHoursToDays(balance.national_holiday_hours),
    },
    {
      label: t('balance.totalHoliday'),
      value: formatHoursToDays(balance.total_holiday_hours),
    },
    {
      label: t('balance.usedHoliday'),
      value: formatHoursToDays(balance.used_holiday_hours),
    },
    {
      label: t('balance.overtimeAdjustment'),
      value: formatHoursToDays(balance.overtime_adjustment),
    },
    {
      label: t('balance.crossYearsSaldo'),
      value: formatHoursToDays(balance.cross_years_saldo),
    },
    {
      label: t('balance.balance'),
      value: formatHoursToDays(balance.balance),
      highlight: true,
    },
  ]

  return (
    <Paper variant="outlined" sx={{ p: 4, borderRadius: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="baseline" spacing={2}>
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {balance.year} &mdash; {balance.contract_hours_per_week}h/week
            &mdash; {balance.work_percentage}%
          </Typography>
        </Stack>

        <Divider />

        <Grid container={true} spacing={3}>
          {stats.map(({ label, value, highlight }) => (
            <Grid key={label} item={true} xs={6} sm={4} md={3}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: highlight ? 'secondary.light' : 'grey.50',
                  border: '1px solid',
                  borderColor: highlight ? 'secondary.main' : 'grey.200',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {label}
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  {value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Paper>
  )
}
